import { useEffect, useRef, useState } from 'react';
import { APIRoutes } from '@/services/api.routes';
import { handleAPIError } from '@/utils/toasts';
import { isValidSourceRef, normalizeSourceRef } from '@/utils/symbol-source';
import type { StockExchangeID, SymbolID } from '@/models';
import type { VerifySymbolResponse } from '@/modelsAPI';

const DETECT_DEBOUNCE_MS = 600;

interface Touched {
  googleSheets: boolean;
  eodhd: boolean;
}

interface Params {
  ticker: string;
  exchangeID: StockExchangeID | '';
  /** The symbol being edited, so it does not count as an already existing symbol. */
  symbolId: SymbolID | null;
  /** Detect the refs after every ticker/exchange change and fill the fields the admin has not edited. */
  autoDetect: boolean;
}

export interface SymbolSourceRefs {
  googleSheetsRef: string;
  eodhdRef: string;
  setGoogleSheetsRef: (value: string) => void;
  setEodhdRef: (value: string) => void;
  result: VerifySymbolResponse | null;
  verifying: boolean;
  /** Every non-empty field matches the ref format the backend accepts. */
  refsValid: boolean;
  /** Auto-detection has not delivered refs for the current ticker/exchange yet. */
  detectionPending: boolean;
  /** POST /symbols values: an empty field is an explicit null unless detection is still pending (omitted = backend default). */
  createRefs: { googleSheetsRef?: string | null; eodhdRef?: string | null };
  /** Verify without refs and overwrite both fields with the detected refs. */
  detect: () => void;
  /** Verify the refs currently typed into the fields. */
  check: () => void;
  reset: (googleSheetsRef: string, eodhdRef: string) => void;
}

/** State of the Google-Sheets/EODHD ref inputs of the symbol create/edit modals, backed by POST /symbols/verify. */
export function useSymbolSourceRefs({ ticker, exchangeID, symbolId, autoDetect }: Params): SymbolSourceRefs {
  const [googleSheetsRef, setGoogleSheetsRefState] = useState('');
  const [eodhdRef, setEodhdRefState] = useState('');
  const [touched, setTouchedState] = useState<Touched>({ googleSheets: false, eodhd: false });
  const [result, setResult] = useState<VerifySymbolResponse | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [detectedKey, setDetectedKey] = useState<string | null>(null);

  // Responses of superseded requests are dropped by comparing against this counter.
  const requestCounter = useRef(0);
  const touchedRef = useRef<Touched>(touched);

  const normalizedTicker = ticker.trim().toUpperCase();
  const key = `${normalizedTicker}|${exchangeID}`;
  const detectionPending = autoDetect && detectedKey !== key;

  const setTouched = (next: Touched) => {
    touchedRef.current = next;
    setTouchedState(next);
  };

  const runVerify = async (mode: 'auto' | 'detect' | 'check') => {
    if (normalizedTicker === '' || exchangeID === '') {
      return;
    }

    const requestId = ++requestCounter.current;
    setVerifying(true);

    try {
      const gs = normalizeSourceRef(googleSheetsRef);
      const eodhd = normalizeSourceRef(eodhdRef);
      const response = await APIRoutes.verifySymbol({
        ticker: normalizedTicker,
        exchangeId: exchangeID,
        symbolId,
        googleSheetsRef: mode === 'check' && gs !== '' ? gs : null,
        eodhdRef: mode === 'check' && eodhd !== '' ? eodhd : null,
      });
      if (requestId !== requestCounter.current) {
        return;
      }

      setResult(response);

      if (mode !== 'check' && response.googleSheets !== null && response.eodhd !== null) {
        const overwrite = mode === 'detect';
        if (overwrite || !touchedRef.current.googleSheets) {
          setGoogleSheetsRefState(response.googleSheets.ref ?? '');
        }
        if (overwrite || !touchedRef.current.eodhd) {
          setEodhdRefState(response.eodhd.ref ?? '');
        }
        if (overwrite) {
          setTouched({ googleSheets: false, eodhd: false });
        }
        setDetectedKey(key);
      }
    } catch (err) {
      if (requestId === requestCounter.current) {
        handleAPIError(err, 'Fehler beim Prüfen der Datenquellen');
      }
    } finally {
      if (requestId === requestCounter.current) {
        setVerifying(false);
      }
    }
  };

  useEffect(() => {
    // Results and refs detected for the previous ticker/exchange must not end up in the request.
    requestCounter.current++;
    setVerifying(false);
    setResult(null);

    if (!autoDetect) {
      return;
    }

    if (!touchedRef.current.googleSheets) {
      setGoogleSheetsRefState('');
    }
    if (!touchedRef.current.eodhd) {
      setEodhdRefState('');
    }

    if (normalizedTicker === '' || exchangeID === '') {
      return;
    }

    const timer = setTimeout(() => { void runVerify('auto'); }, DETECT_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [autoDetect, key]);

  const refForCreate = (value: string, isTouched: boolean): string | null | undefined => {
    const ref = normalizeSourceRef(value);
    if (ref !== '') {
      return ref;
    }
    return !isTouched && detectionPending ? undefined : null;
  };

  return {
    googleSheetsRef,
    eodhdRef,
    setGoogleSheetsRef: (value: string) => {
      setGoogleSheetsRefState(value);
      setTouched({ ...touchedRef.current, googleSheets: true });
    },
    setEodhdRef: (value: string) => {
      setEodhdRefState(value);
      setTouched({ ...touchedRef.current, eodhd: true });
    },
    result,
    verifying,
    refsValid: (normalizeSourceRef(googleSheetsRef) === '' || isValidSourceRef('GSHEETS', googleSheetsRef)) &&
               (normalizeSourceRef(eodhdRef) === '' || isValidSourceRef('EODHD', eodhdRef)),
    detectionPending,
    createRefs: {
      googleSheetsRef: refForCreate(googleSheetsRef, touched.googleSheets),
      eodhdRef: refForCreate(eodhdRef, touched.eodhd),
    },
    detect: () => { void runVerify('detect'); },
    check: () => { void runVerify('check'); },
    reset: (initialGoogleSheetsRef: string, initialEodhdRef: string) => {
      requestCounter.current++;
      setGoogleSheetsRefState(initialGoogleSheetsRef);
      setEodhdRefState(initialEodhdRef);
      setTouched({ googleSheets: false, eodhd: false });
      setResult(null);
      setVerifying(false);
      setDetectedKey(null);
    },
  };
}
